import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import InfoTooltipButton from "./InfoTooltipButton";

import ReactTable from "../../components/ReactTable/ReactTable.js";
import CardHeader from "../../components/Card/CardHeader";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardText from "../../components/Card/CardText";
import useCustomStyles from "../../customAssets/customStyles";

import { useTranslation } from "react-i18next";

/**
 * Tooltips permitidos por tipo de tabla.
 */
const HEADER_INFO = {
  permisos: "customtable-tooltip-permisos",
  urls: "customtable-tooltip-urls",
  librerias: "customtable-tooltip-librerias",
  codigo: "customtable-tooltip-codigo",
  niap: "customtable-tooltip-niap",
};

const useStyles = makeStyles((theme) => ({
  /**
   * La pastilla visual real del header es CardText.
   * El contenido del título y el icono deben vivir dentro.
   */
  headerBadge: {
    display: "block",
    width: "fit-content",
    maxWidth: "100%",
  },

  /**
   * Cabecera responsive dentro de la propia pastilla turquesa.
   */
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    maxWidth: "100%",
  },

  headerTitleWrap: {
    minWidth: 0,
    flex: 1,
  },

  headerTitle: {
    margin: 0,
    color: "#fff",
    fontWeight: 400,
    fontSize: "1rem",
    wordBreak: "break-word",

    [theme.breakpoints.up("md")]: {
      fontSize: "1.2rem",
    },
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },

  /**
   * Botón circular de ayuda, con caja fija y centrado real.
   */
  infoIconWrapper: {
    width: 30,
    height: 30,
    padding: 0,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.16)",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.24)",
    },
  },

  /**
   * Ajuste fino para que la "i" no quede baja dentro del círculo.
   */
  infoIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
    display: "block",
    position: "relative",
    top: "-2px",
  },

  tooltip: {
    fontSize: "0.9rem",
    padding: "12px 16px",
    maxWidth: 320,
  },

  arrow: {},

  /**
   * Body algo más compacto en móvil.
   */
  cardBody: {
    padding: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },

  /**
   * Wrapper horizontal para tablas anchas.
   * Esto evita que una tabla rompa el viewport en móvil.
   */
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
}));

const CustomTableInfo = ({ data, columns, titulo, infoKey }) => {
  const classes = useStyles();
  const customClasses = useCustomStyles();
  const { t } = useTranslation("search");

  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }

  const tooltipKey = infoKey && HEADER_INFO[infoKey] ? HEADER_INFO[infoKey] : null;
  const tooltipText = tooltipKey ? t(tooltipKey, { defaultValue: "" }) : "";

  const titleClassName = [classes.headerTitle, customClasses.cardTitle]
    .filter(Boolean)
    .join(" ");

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardText color="primary" className={classes.headerBadge}>
              <div className={classes.headerContent}>
                <div className={classes.headerTitleWrap}>
                  <h4 className={titleClassName}>{t(titulo)}</h4>
                </div>

                {tooltipText ? (
                  <div className={classes.headerActions}>
                    <InfoTooltipButton
                      title={tooltipText}
                      placement="right"
                      tooltipClasses={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                      iconButtonClassName={classes.infoIconWrapper}
                      iconClassName={classes.infoIcon}
                    />
                  </div>
                ) : null}
              </div>
            </CardText>
          </CardHeader>

          <CardBody className={classes.cardBody}>
            <div className={classes.tableWrapper}>
              <ReactTable data={data} columns={columns} />
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

CustomTableInfo.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  titulo: PropTypes.string.isRequired,
  infoKey: PropTypes.string,
};

export default CustomTableInfo;